import {
  PlusOutlined,
  PushpinOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  GetGeocodingParticelleCatastaliParams,
  getGeocodingParticelleCatastali,
} from '@framework/api-client';
import {
  FormHandler,
  FormItem,
  ManagedSubmitButton,
  NumberInput,
  SubmitButton,
  TextInput,
} from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { ModalDialog, RowElement } from '@framework/ui';
import { yup } from '@framework/validation';
import { Button, Space, Tooltip, message } from 'antd';
import { Map } from 'ol';
import { GeoJSON } from 'ol/format';
import * as React from 'react';
import { useState } from 'react';
import { Subject } from 'rxjs';
import type { MapSubjectEvent } from '../MapWidgetComponent';
import { MapWidgetLogic } from '../MapWidgetLogic';
import { MapMunicipalitySelectInput } from './MapMunicipalitySelectInput';

export interface IMapSearchCadastralControlProps {
  map: React.RefObject<Map>;
  mapSubject: React.RefObject<Subject<MapSubjectEvent>>;

  isMobile?: boolean | null;
}

const geoJSON = new GeoJSON({ featureProjection: 'EPSG:3857' });

const SearchCadastralControlSchema: yup.ObjectSchema<
  GetGeocodingParticelleCatastaliParams & { select: boolean }
> = yup.object({
  codiceComune: yup.string().required().label('Comune'),
  codiceSezione: yup.string().label('Sezione catastale'),
  codiceFoglio: yup
    .number()
    .transform(t => t ?? undefined)
    .label('Foglio catastale'),
  numero: yup
    .number()
    .transform(t => t ?? undefined)
    .label('Particella catastale'),
  codiceAllegato: yup.string().label('Allegato catastale'),
  codiceSviluppo: yup.string().label('Sviluppo catastale'),
  page: yup.number().default(0).label('Pagina'),
  maxNumResults: yup.number().default(10).label('Numero massimo di risultati'),
  select: yup.boolean().default(false).label('Seleziona'),
});

export function MapSearchCadastralControl(
  props: IMapSearchCadastralControlProps,
) {
  const { map, mapSubject, isMobile } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const buttonText = t(
    'mappa.controlli.cerca-particella.pulsante.testo',
    'Particella',
  );
  const dialogTitle = t(
    'mappa.controlli.cerca-particella.modale.titolo',
    'Cerca per particella catastale',
  );

  return (
    <div>
      {MapWidgetLogic.showCadastralParticelsAndIntersections() && (
        <Tooltip title={dialogTitle}>
          <Button
            style={{ borderWidth: '1px' }}
            icon={<PushpinOutlined aria-hidden="true" />}
            onClick={() => setIsOpen(true)}
            aria-label={isMobile ? buttonText : undefined}
          >
            {!isMobile && buttonText}
          </Button>
        </Tooltip>
      )}

      <ModalDialog
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        size="small"
        title={dialogTitle}
        closable
        footer={null}
      >
        <FormHandler
          schema={SearchCadastralControlSchema}
          onSubmit={async values => {
            console.log(values);
            try {
              const cadastralParticles = await getGeocodingParticelleCatastali(
                values,
              );
              console.log(cadastralParticles);
              if (cadastralParticles.features?.length === 0) {
                message.info(
                  t(
                    'mappa.controlli.cerca-particella.modale.form.errore.nessun-risultato',
                    'Nessun risultato trovato',
                  ),
                );
                return;
              }

              const feature = geoJSON.readFeature(
                cadastralParticles.features?.[0],
              );
              if (!feature) {
                message.error(
                  t(
                    'mappa.controlli.cerca-particella.modale.form.errore.nessuna-geometria-valida',
                    'Errore durante la ricerca. Nessuna geometria valida trovata',
                  ),
                );
                console.error('Errore durante la ricerca. Nessuna geometria valida trovata', cadastralParticles); // prettier-ignore
                return;
              }

              map.current?.getView().fit(feature.getGeometry()!.getExtent(), {
                padding: [16, 16, 16, 16],
                duration: 400,
              });

              if (values.select) {
                // Seleziona la particella
                mapSubject.current?.next({
                  type: 'add',
                  feature: feature!,
                  addToSource: true,
                });
              }
              setIsOpen(false);
            } catch (error) {
              message.error(
                t(
                  'mappa.controlli.cerca-particella.modale.form.errore.durante-la-ricerca',
                  'Si è verificato un errore durante la ricerca',
                ),
              );
              console.error(error);
            }
          }}
          nested
          layout="vertical"
        >
          <div>
            <RowElement>
              <FormItem
                name="codiceComune"
                label={t(
                  'mappa.controlli.cerca-particella.modale.form-label.comune',
                  'Comune',
                )}
              >
                <MapMunicipalitySelectInput name="codiceComune" />
              </FormItem>
              <FormItem
                name="codiceSezione"
                label={t(
                  'mappa.controlli.cerca-particella.modale.form-label.sezione-catastale',
                  'Sezione catastale',
                )}
              >
                <TextInput name="codiceSezione" />
              </FormItem>
            </RowElement>
            <RowElement>
              <FormItem
                name="codiceFoglio"
                label={t(
                  'mappa.controlli.cerca-particella.modale.form-label.foglio-catastale',
                  'Foglio catastale',
                )}
              >
                <NumberInput name="codiceFoglio" />
              </FormItem>
              <FormItem
                name="numero"
                label={t(
                  'mappa.controlli.cerca-particella.modale.form-label.particella-catastale',
                  'Particella catastale',
                )}
              >
                <NumberInput name="numero" />
              </FormItem>
            </RowElement>
            <RowElement>
              <FormItem
                name="codiceAllegato"
                label={t(
                  'mappa.controlli.cerca-particella.modale.form-label.allegato-catastale',
                  'Allegato catastale',
                )}
              >
                <TextInput name="codiceAllegato" />
              </FormItem>
              <FormItem
                name="codiceSviluppo"
                label={t(
                  'mappa.controlli.cerca-particella.modale.form-label.sviluppo-catastale',
                  'Sviluppo catastale',
                )}
              >
                <TextInput name="codiceSviluppo" />
              </FormItem>
            </RowElement>
            <Space>
              <SubmitButton
                type="default"
                block
                icon={<SearchOutlined aria-hidden />}
              >
                {t(
                  'mappa.controlli.cerca-particella.modale.form-button.cerca',
                  'Cerca',
                )}
              </SubmitButton>
              <ManagedSubmitButton
                transformOnSubmit={data => {
                  data.select = true;
                }}
                type="primary"
                size="large"
                block
                iconLeftComponent={PlusOutlined}
              >
                {t(
                  'mappa.controlli.cerca-particella.modale.form-button.seleziona',
                  'Seleziona',
                )}
              </ManagedSubmitButton>
            </Space>
          </div>
        </FormHandler>
      </ModalDialog>
    </div>
  );
}
